import {
  List,
  TextField,
  SimpleList,
  Datagrid,
  useNotify,
  ReferenceField,
  Title,
  TopToolbar,
  Button,
  DateField,
  FunctionField,
  useStore,
} from "react-admin";
import AddIcon from "@mui/icons-material/Add";

import { Box, Typography, Paper, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomFilter from "features/CustomFilter";
import { APPLICATIONS, CAMPAIGNS, MOBILE_BREAKPOINT, SpecialistServicePath } from "utils/constants";

import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

import campaignApplicationFilters from "./ListFilters";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import UserField from "server/components/Fields/UserField";
import EmployeeField from "server/components/Fields/EmployeeField";
import DetailsButton from "./Show/DetailsButton";
import { specialistRequests } from "api/specialistApi";
import { useNotifyError } from "utils/useNotifyError";
import dataProvider from "server/dataProvider";

const ListCampaignApplications = () => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const navigate = useNavigate();

  const [campaignsOptions, setCampaignsOptions] = useState([]);
  const STORE_KEY = "campaign-applications-specialists";

  // filters drives the <List> and the gate (persisted via useStore);
  // campaignId is the draft selection bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [campaignId, setCampaignId] = useState(filters?.campaignId ?? null);

  useEffect(() => {
    (async () => {
      try {
        const res = await dataProvider.customRequest(specialistRequests.getSpecialistCampaignsSelect());
        setCampaignsOptions(res?.data ?? []);
      } catch (error) {
        notifyError(error);
      }
    })();
  }, [notifyError]);

  const searchParams = () => {
    if (campaignId) {
      setFilters({ campaignId });
    } else {
      notify("Моля въведете кампания!", { type: "error" });
    }
  };

  const titleHeader = `Кандидатури за кампания`;
  const CampaignApplicationTopToolbar = () => (
    <TopToolbar
      sx={{
        gap: "1rem",
        justifyContent: "center",
        padding: "2rem",
        background: "#a5a1a121",
        marginBottom: "1rem",
        marginTop: "1rem",
      }}
    >
      <SecuredComponent permission={permissions.canManageSpecialistCampaignsApplications}>
        <Button
          variant="contained"
          label="Създай кандидатура "
          onClick={() =>
            navigate(`/${SpecialistServicePath}${APPLICATIONS}/create`, {
              state: { campaignId: filters.campaignId },
            })
          }
        />
      </SecuredComponent>
    </TopToolbar>
  );
  return (
    <>
      {!filters && <Title title={titleHeader} />}
      <CustomFilter
        title="Списък кампании"
        filters={[
          {
            label: "Кампания",
            value: campaignId,
            setValue: setCampaignId,
            type: "autocomplete",
            multiple: false,
            choices: campaignsOptions,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          resource={`${SpecialistServicePath}${CAMPAIGNS}/${filters.campaignId}/${APPLICATIONS}`}
          actions={
            <SecuredComponent permission={permissions.canManageSpecialistCampaignsApplications}>
              <Button
                variant="text"
                label="Създай  "
                startIcon={<AddIcon />}
                sx={{ display: "flex", justifySelf: "end" }}
                onClick={() =>
                  navigate(`/${SpecialistServicePath}${APPLICATIONS}/create`, {
                    state: { campaignId: filters.campaignId },
                  })
                }
              />
            </SecuredComponent>
          }
          filters={campaignApplicationFilters}
          title={titleHeader}
          sx={settings.style.blockStyle}
          empty={<CampaignApplicationTopToolbar />}
        >
          {isMobile ? (
            <SimpleList
              empty={<CampaignApplicationTopToolbar />}
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => <MobileListRow fields={[{ label: "ID", value: record.id }]} />}
              secondaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Статус", value: record.statusNameBg }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Кампания",
                        value: (
                          <ReferenceField
                            label="Кампания"
                            source="campaignId"
                            reference={specialistRequests.getSpecialistCampaignsSelect()}
                            sortable={false}
                          >
                            <TextField source="nameBg" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Кандидат",
                        value: (
                          <UserField
                            label="Кандидат"
                            source="candidateUserId"
                            sortable={false}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Служител",
                        value: (
                          <EmployeeField
                            label="Служител"
                            source="employeeId"
                            sortable={false}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Университет",
                        value: (
                          <TextField
                            label="Университет"
                            source="university"
                            sortable={false}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Дата на кандидатстване",
                        value: (
                          <DateField
                            source="createdOn"
                            label="Дата на кандидатстване"
                            showTime
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              tertiaryText={rec => <DetailsButton rec={rec} />}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={settings.style.firstExpandIconDatagrid}
            >
              <TextField
                source="id"
                label="ID"
                sortable={false}
              />

              <ReferenceField
                label="Кампания"
                source="campaignId"
                reference={specialistRequests.getSpecialistCampaignsSelect()}
                sortable={false}
              >
                <TextField source="nameBg" />
              </ReferenceField>
              <TextField
                source="statusNameBg"
                label="Статус"
                sortable={false}
              />
              <UserField
                label="Кандидат"
                source="candidateUserId"
                sortable={false}
              />
              <EmployeeField
                label="Служител"
                source="employeeId"
                sortable={false}
              />
              <TextField
                label="Университет"
                source="university"
                sortable={false}
              />
              <DateField
                source="createdOn"
                label="Дата на кандидатстване"
                showTime
                locales="bg-BG"
              />
              <FunctionField
                render={rec => (
                  <DetailsButton
                    rec={rec}
                    label="Детайли"
                  />
                )}
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};
export default ListCampaignApplications;
